import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle, Mail, Sparkles, Brain, Flame, Sprout,
  Droplets, Wind, Bug, Calendar, RotateCcw, HeartPulse, Activity,
} from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';
import { sections, scaleOptions, getBand, finalReflectionOptions, deeperDive } from '../data/bodyAssessment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = 'https://www.wellnessliving.com/rs/event/breathing_room-rws2se?k_class=981136&k_class_tab=75840';

const iconMap = { Brain, Flame, Sprout, Droplets, Wind, Bug, HeartPulse, Activity };
const accentBar = {
  violet: 'bg-violet-500', rose: 'bg-rose-500', amber: 'bg-amber-500',
  teal: 'bg-teal-500', sky: 'bg-sky-500', emerald: 'bg-emerald-500',
  indigo: 'bg-indigo-500', slate: 'bg-slate-500',
};
const accentSoft = {
  violet: 'bg-violet-50 text-violet-700', rose: 'bg-rose-50 text-rose-700',
  amber: 'bg-amber-50 text-amber-700', teal: 'bg-teal-50 text-teal-700',
  sky: 'bg-sky-50 text-sky-700', emerald: 'bg-emerald-50 text-emerald-700',
  indigo: 'bg-indigo-50 text-indigo-700', slate: 'bg-slate-100 text-slate-700',
};
const bandBadge = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  orange: 'bg-orange-100 text-orange-700',
  rose: 'bg-rose-100 text-rose-700',
};

// Flatten all questions across sections, then append optional deeper-dive
const flatQuestions = [];
sections.forEach((s, si) => {
  s.questions.forEach((q, qi) => {
    flatQuestions.push({ sectionIndex: si, sectionKey: s.key, sectionName: s.name, accent: s.accent, icon: s.icon, text: q, flatIndex: flatQuestions.length, qNumInSection: qi + 1 });
  });
});
const DEEPER_START = flatQuestions.length; // first deeper-dive flat index
deeperDive.questions.forEach((q, qi) => {
  flatQuestions.push({ sectionKey: deeperDive.key, sectionName: deeperDive.name, accent: deeperDive.accent, icon: deeperDive.icon, text: q, flatIndex: flatQuestions.length, qNumInSection: qi + 1, isDeeper: true });
});

const BodyAssessmentPage = () => {
  const [stage, setStage] = useState('landing');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [reflection, setReflection] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const sectionResults = useMemo(() => {
    return sections.map((s, si) => {
      let score = 0;
      for (let qi = 0; qi < s.questions.length; qi++) {
        const flat = si * 5 + qi;
        score += answers[flat] ?? 0;
      }
      return { ...s, score, band: getBand(score) };
    });
  }, [answers]);

  const ranked = useMemo(() => [...sectionResults].sort((a, b) => b.score - a.score), [sectionResults]);
  const highLoad = ranked.filter((r) => r.band.key === 'high');
  const primary = highLoad.length > 0 ? highLoad.slice(0, 2) : ranked.slice(0, 1);

  const deeperDiveScore = useMemo(() => {
    let s = 0;
    for (let i = 0; i < deeperDive.questions.length; i++) s += answers[DEEPER_START + i] ?? 0;
    return s;
  }, [answers]);
  const deeperAnswered = useMemo(
    () => deeperDive.questions.some((_, i) => answers[DEEPER_START + i] !== undefined),
    [answers]
  );

  const handleSelect = (value) => {
    setAnswers((prev) => ({ ...prev, [current]: value }));
    setTimeout(() => {
      if (current < flatQuestions.length - 1) setCurrent((c) => c + 1);
      else setStage('result');
    }, 180);
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const resultStr = primary.map((p) => p.name).join(', ');
    const scoreMap = {};
    sectionResults.forEach((s) => { scoreMap[s.name] = { score: s.score, band: s.band.label }; });
    try {
      await fetch(`${BACKEND_URL}/api/quiz-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          quiz_type: 'wellness_pattern_assessment',
          result: resultStr,
          answers: { scores: scoreMap, reflection, responses: answers, deeperDive: { score: deeperDiveScore, max: 20, answered: deeperAnswered } },
          created_at: new Date().toISOString(),
        }),
      });
      setEmailSent(true);
    } catch (err) {
      console.error('Error saving assessment:', err);
      setEmailSent(true);
    }
    setSubmitting(false);
  };

  const restart = () => {
    setAnswers({}); setCurrent(0); setReflection(''); setEmail(''); setEmailSent(false); setStage('landing');
  };

  const q = flatQuestions[current];
  const QIcon = q ? iconMap[q.icon] : null;
  const progress = ((current + 1) / flatQuestions.length) * 100;

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="body-assessment-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8" data-testid="back-to-programs">
          <ArrowLeft size={18} />
          <span className="font-body text-sm">Back to Programs</span>
        </Link>

        <AnimatePresence mode="wait">
          {/* LANDING */}
          {stage === 'landing' && (
            <motion.div key="landing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="text-center">
              <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">A Wellness Pattern Assessment</p>
              <h1 className="font-heading text-4xl md:text-5xl text-forest leading-tight mb-4">What Is Your Body Asking For?</h1>
              <p className="text-stone-600 font-body text-lg mb-6 max-w-2xl mx-auto">
                Thirty-five short statements across seven body systems, plus a few optional deeper-dive questions.
                Your answers reveal where your body may be carrying the most load — and which direction of support
                could make the biggest difference.
              </p>
              <div className="bg-white rounded-2xl p-6 border border-stone-100 text-left max-w-xl mx-auto mb-8">
                <p className="font-body text-forest font-medium mb-2">How it works</p>
                <p className="font-body text-stone-600 text-sm leading-relaxed">
                  For each statement, choose the answer that best reflects your experience over the <strong>past 30 days</strong>
                  &nbsp;— from <em>Never</em> to <em>Almost Always</em>. It takes about 4 minutes. Your results are shown instantly,
                  and you can optionally share them with Jill.
                </p>
              </div>
              <button
                onClick={() => setStage('quiz')}
                className="inline-flex items-center gap-2 bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors"
                data-testid="start-assessment-btn"
              >
                Begin the Assessment <ArrowRight size={18} />
              </button>
              <div className="mt-10"><BreathingRoomAttribution /></div>
            </motion.div>
          )}

          {/* QUIZ */}
          {stage === 'quiz' && q && (
            <motion.div key={`q-${current}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-body font-medium ${accentSoft[q.accent]}`} data-testid="section-label">
                    {QIcon && <QIcon size={13} />} {q.sectionName}
                  </span>
                  <span className="font-body text-stone-400 text-sm">{current + 1} / {flatQuestions.length}</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className={`h-full ${accentBar[q.accent]} transition-all duration-300`} style={{ width: `${progress}%` }} />
                </div>
              </div>

              <h2 className="font-heading text-2xl md:text-3xl text-forest leading-snug mb-3" data-testid="question-text">{q.text}</h2>
              {q.isDeeper && current === DEEPER_START && (
                <p className="font-body text-stone-500 text-sm mb-5 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3" data-testid="deeper-dive-intro">{deeperDive.intro}</p>
              )}
              {!(q.isDeeper && current === DEEPER_START) && <div className="mb-5" />}

              <div className="space-y-3">
                {scaleOptions.map((opt) => {
                  const selected = answers[current] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleSelect(opt.value)}
                      className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 font-body transition-all text-left ${
                        selected ? 'border-forest bg-forest/5 text-forest' : 'border-stone-200 bg-white text-stone-700 hover:border-forest/40'
                      }`}
                      data-testid={`answer-option-${opt.value}`}
                    >
                      <span className="font-medium">{opt.label}</span>
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selected ? 'border-forest bg-forest' : 'border-stone-300'}`}>
                        {selected && <CheckCircle size={14} className="text-cream" />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={() => current > 0 ? setCurrent((c) => c - 1) : setStage('landing')}
                  className="inline-flex items-center gap-2 text-stone-500 hover:text-forest font-body text-sm transition-colors"
                  data-testid="prev-question-btn"
                >
                  <ArrowLeft size={16} /> Back
                </button>
                {q.isDeeper && (
                  <button
                    onClick={() => setStage('result')}
                    className="inline-flex items-center gap-2 text-stone-500 hover:text-forest font-body text-sm transition-colors"
                    data-testid="skip-deeper-btn"
                  >
                    Skip to results <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* RESULT */}
          {stage === 'result' && (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} data-testid="assessment-result">
              <div className="text-center mb-10">
                <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-3">Your Results</p>
                <h1 className="font-heading text-3xl md:text-4xl text-forest mb-3">What Your Body Is Asking For</h1>
                <p className="text-stone-600 font-body max-w-xl mx-auto">
                  Here's where your body may be carrying the most load right now. There are no bad scores — only clues about where to begin.
                </p>
              </div>

              {/* Primary direction */}
              <div className="bg-gradient-to-br from-forest to-forest-dark text-cream rounded-2xl p-8 mb-10" data-testid="primary-direction">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={20} className="text-terracotta" />
                  <h2 className="font-heading text-2xl">Your Primary Direction</h2>
                </div>
                <p className="font-body text-cream/80 mb-5">
                  {highLoad.length > 0
                    ? 'These systems scored in the High Load range — supporting them first may ripple out to everything else.'
                    : 'No system landed in High Load — wonderful. This is the area showing the most signal right now.'}
                </p>
                <div className="space-y-4">
                  {primary.map((p) => {
                    const PIcon = iconMap[p.icon];
                    return (
                      <div key={p.key} className="bg-cream/10 rounded-xl p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="inline-flex items-center gap-2 font-heading text-lg text-cream">{PIcon && <PIcon size={18} className="text-terracotta" />} {p.name}</span>
                          <span className="font-body text-sm text-cream/90">{p.score} / 20 · {p.band.label}</span>
                        </div>
                        <p className="font-body text-cream/85 text-sm leading-relaxed mb-3">{p.interpretation}</p>
                        <div className="flex flex-wrap gap-2">
                          {p.support.map((sup) => (
                            <span key={sup} className="px-2.5 py-1 bg-cream/15 rounded-full text-xs font-body text-cream">{sup}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {deeperAnswered && deeperDiveScore >= 8 && (
                  <div className="mt-5 bg-cream/10 border border-cream/20 rounded-xl p-5" data-testid="primary-deeper-flag">
                    <span className="inline-flex items-center gap-2 font-heading text-lg text-cream mb-2">
                      <Activity size={18} className="text-terracotta" /> Also worth exploring: Recovery & Resilience
                    </span>
                    <p className="font-body text-cream/85 text-sm leading-relaxed">
                      Your optional deeper-dive answers ({deeperDiveScore}/20) suggest your body may respond strongly to standing,
                      exertion, heat, or day-to-day variability — patterns that can follow illness or prolonged stress. This isn't
                      a diagnosis; it's a gentle nudge to prioritize <span className="text-cream font-medium">pacing and recovery</span>,
                      and to mention it to a trusted practitioner if it persists.
                    </p>
                  </div>
                )}
              </div>

              {/* Full readout */}
              <h3 className="font-heading text-xl text-forest mb-4">Your Full Pattern</h3>
              <div className="space-y-4 mb-10">
                {ranked.map((s) => {
                  const SIcon = iconMap[s.icon];
                  return (
                    <div key={s.key} className="bg-white rounded-xl p-5 border border-stone-100" data-testid={`section-result-${s.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="inline-flex items-center gap-2 font-body font-medium text-forest">{SIcon && <SIcon size={16} className={accentSoft[s.accent].split(' ')[1]} />} {s.name}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-body font-medium ${bandBadge[s.band.color]}`}>{s.band.label} · {s.score}/20</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden mb-3">
                        <div className={`h-full ${accentBar[s.accent]}`} style={{ width: `${(s.score / 20) * 100}%` }} />
                      </div>
                      <p className="font-body text-stone-600 text-sm leading-relaxed mb-2">{s.band.copy}</p>
                      <p className="font-body text-stone-500 text-sm leading-relaxed mb-3"><span className="text-forest font-medium">{s.interpretation}</span></p>
                      {s.closing && (
                        <p className="font-body text-terracotta text-sm italic mb-3">{s.closing}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs font-body text-stone-400 mr-1 self-center">Potential support:</span>
                        {s.support.map((sup) => (
                          <span key={sup} className={`px-2.5 py-1 rounded-full text-xs font-body ${accentSoft[s.accent]}`}>{sup}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Deeper-dive signal */}
              {deeperAnswered && (
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 mb-10" data-testid="deeper-dive-signal">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity size={18} className="text-slate-500" />
                    <h3 className="font-heading text-lg text-forest">Deeper-Dive Signal</h3>
                    <span className="ml-auto font-body text-sm text-slate-500">{deeperDiveScore}/20</span>
                  </div>
                  <p className="font-body text-stone-600 text-sm leading-relaxed">
                    {deeperDiveScore >= 8
                      ? 'Your optional responses suggest it may be worth gently exploring how your body responds to standing, exertion, heat, and day-to-day variability — patterns that sometimes show up after illness or prolonged stress. This is simply a signal to pace yourself and, if it persists, mention it to a trusted practitioner.'
                      : 'Thanks for sharing these. Nothing here stands out strongly right now — they simply add helpful context to your overall picture.'}
                  </p>
                </div>
              )}

              {/* Final reflection */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100 mb-10" data-testid="final-reflection">
                <h3 className="font-heading text-xl text-forest mb-1">Final Reflection</h3>
                <p className="font-body text-stone-500 text-sm mb-4">Which statement feels most true right now?</p>
                <div className="space-y-2">
                  {finalReflectionOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setReflection(opt)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left font-body text-sm transition-all ${
                        reflection === opt ? 'border-forest bg-forest/5 text-forest' : 'border-stone-200 text-stone-600 hover:border-forest/40'
                      }`}
                      data-testid="reflection-option"
                    >
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${reflection === opt ? 'border-forest bg-forest' : 'border-stone-300'}`} />
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional email capture */}
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100 mb-8" data-testid="email-capture">
                {emailSent ? (
                  <div className="text-center py-2">
                    <CheckCircle size={28} className="text-emerald-500 mx-auto mb-3" />
                    <p className="font-heading text-lg text-forest mb-1">Thank you — your results are saved</p>
                    <p className="font-body text-stone-500 text-sm">Jill has received your results and may reach out with a few thoughts on your best next direction.</p>
                  </div>
                ) : (
                  <form onSubmit={handleEmail}>
                    <div className="flex items-center gap-2 mb-2">
                      <Mail size={18} className="text-terracotta" />
                      <h3 className="font-heading text-lg text-forest">Send my results to Jill (optional)</h3>
                    </div>
                    <p className="font-body text-stone-500 text-sm mb-4">Share your results with Jill so she can support your next steps — especially helpful if you're considering the Flex Detox. Be sure to screenshot or note your results above for your own records.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="flex-1 px-4 py-3 rounded-full border border-stone-200 font-body focus:outline-none focus:border-forest"
                        data-testid="email-input"
                      />
                      <button
                        type="submit"
                        disabled={submitting}
                        className="bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-60"
                        data-testid="email-submit-btn"
                      >
                        {submitting ? 'Sending…' : 'Share With Jill'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* CTAs */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <Link to="/programs/flex-detox" className="bg-white border border-stone-200 rounded-2xl p-5 hover:border-forest/40 transition-colors text-center" data-testid="cta-flex-detox">
                  <Sparkles size={22} className="text-terracotta mx-auto mb-2" />
                  <p className="font-heading text-lg text-forest mb-1">Explore the Flex Detox</p>
                  <p className="font-body text-stone-500 text-sm">A flexible, choose-your-depth 21-day terrain reset.</p>
                </Link>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="bg-white border border-stone-200 rounded-2xl p-5 hover:border-forest/40 transition-colors text-center" data-testid="cta-info-session">
                  <Calendar size={22} className="text-terracotta mx-auto mb-2" />
                  <p className="font-heading text-lg text-forest mb-1">Join a Free Info Session</p>
                  <p className="font-body text-stone-500 text-sm">Bring your results and find your best direction together.</p>
                </a>
              </div>

              <div className="text-center">
                <button onClick={restart} className="inline-flex items-center gap-2 text-stone-500 hover:text-forest font-body text-sm transition-colors" data-testid="restart-btn">
                  <RotateCcw size={15} /> Retake the assessment
                </button>
              </div>

              <p className="text-xs font-body text-stone-400 italic text-center mt-8 max-w-xl mx-auto">
                This assessment is for self-reflection and education — it is not a diagnosis. If you have health concerns, please consult a qualified healthcare provider.
              </p>
              <div className="mt-8"><BreathingRoomAttribution /></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BodyAssessmentPage;
